/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	importSchema,
	ISchemaConstObject,
	ISchemaValue,
	TSchemaConstObjectProps,
	loadCompiledModel
} from "@adapptio/lib-blueprint";
import { IViewInstance, IViewInstanceResolver, RESOURCE_TYPES } from "@adapptio/lib-core";
import { createEventEmitter, emitEvent, offEvent, onEvent } from "@adapptio/lib-shared";
import { IEditorApiService } from "../../api/IEditorApiService";
import { BrowserLogger } from "../../common/BrowserLogger";
import { ResourceService } from "../../resources/ResourceService";
import { TEditorResource } from "../../resources/ResourceTypes";

interface IViewCacheEntry {
	instance: IViewInstance;
	lastModified: number;
}

/**
 * View Instance Resolver
 */
export class ViewInstanceResolver implements IViewInstanceResolver {
	/** Cache of resolved views */
	private viewCache: { [K: string]: IViewCacheEntry } = {};

	/** Invalidate event */
	public onInvalidate = createEventEmitter<{ viewId?: string }>();

	private typeInvalidateHandler: (resourceType: RESOURCE_TYPES) => void;
	private contentInvalidateHandler: (resource: TEditorResource) => void;

	private invalidateTimeout;
	private invalidateIds = new Set<string>();

	/**
	 * Constructor
	 */
	public constructor(
		private logger: BrowserLogger,
		private apiSvc: IEditorApiService,
		private resourceSvc: ResourceService
	) {
		this.typeInvalidateHandler = (resourceType) => {
			if (resourceType === RESOURCE_TYPES.VIEW) {
				this.invalidate();
			}
		};

		this.contentInvalidateHandler = (resource) => {
			if (resource.resourceType === RESOURCE_TYPES.VIEW) {
				this.invalidate(resource.id);
			}
		};

		onEvent(this.resourceSvc.onResourceTypeChange, this.typeInvalidateHandler);
		onEvent(this.resourceSvc.onResourceContentChange, this.contentInvalidateHandler);
	}

	/**
	 * Returns view instance by given viewId
	 * Resolves null if view is not found.
	 */
	public async getInstance(viewId: string): Promise<IViewInstance> {
		// Get resource
		const resource = this.resourceSvc.getResourceById(viewId);

		if (!resource || resource.resourceType !== RESOURCE_TYPES.VIEW) {
			return null;
		}

		// Check cache
		const cacheEntry = this.viewCache[viewId];

		if (cacheEntry && cacheEntry.lastModified === resource.descriptor.lastModified) {
			return cacheEntry.instance;
		}

		// Resolve instance
		const viewInstance = await this.loadView(viewId);

		if (viewInstance) {
			this.viewCache[viewId] = {
				instance: viewInstance,
				lastModified: resource.descriptor.lastModified
			};

			return viewInstance;
		} else {
			return null;
		}
	}

	/**
	 * Loads view and returns instance
	 *
	 * @param viewId View ID
	 */
	private async loadView(viewId: string): Promise<IViewInstance> {
		try {
			const viewData = await this.apiSvc.getView(viewId);

			let paramsSchema: IViewInstance["params"];
			let renderFn: IViewInstance["renderFn"];

			try {
				paramsSchema = importSchema<ISchemaValue<ISchemaConstObject<TSchemaConstObjectProps>>>(
					viewData.paramsSchema
				).constSchema.opts.props;
			} catch (err) {
				this.logger.error(`Failed to import params schema for view ID '${viewId}':`, err);
				return null;
			}

			try {
				renderFn = loadCompiledModel(viewData.code);
			} catch (err) {
				this.logger.error(`Failed to load compiled model for view ID '${viewId}':`, err);
				return null;
			}

			return {
				params: paramsSchema,
				renderFn: renderFn,
				isValid: viewData.isValid
			};
		} catch (err) {
			this.logger.error(`Failed to load view ID '${viewId}' via API:`, err);
			return null;
		}
	}

	private invalidate(viewId?: string): void {
		if (viewId) {
			this.invalidateIds.add(viewId);
		}

		if (this.invalidateTimeout) {
			return;
		}

		this.invalidateTimeout = setTimeout(() => {
			const viewIds = Array.from(this.invalidateIds);

			this.invalidateTimeout = null;
			this.invalidateIds = new Set();

			if (viewIds.length > 0) {
				for (let i = 0; i < viewIds.length; i++) {
					emitEvent(this.onInvalidate, { viewId: viewIds[i] });
				}
			} else {
				emitEvent(this.onInvalidate, { viewId: null });
			}
		}, 0);
	}

	/**
	 * Disposes resolver
	 */
	public dispose(): void {
		offEvent(this.resourceSvc.onResourceTypeChange, this.typeInvalidateHandler);
		offEvent(this.resourceSvc.onResourceContentChange, this.contentInvalidateHandler);
		this.viewCache = {};
	}
}
